/**
 * StillLookingInstructorBanner — calm recovery card for pupils who
 * tapped "Find me an instructor" then drifted away from the connect
 * flow without matching.
 *
 * Operator brief (May 14, 2026):
 *   Right now if a pupil picks "Find me an instructor" we set their
 *   track to instructor_standard and route them to /learn/connect —
 *   but if they bail before completing a match they sit in limbo with
 *   no instructor linked and no obvious next step. This banner gives
 *   them a calm way back in (or out).
 *
 * Visibility — server-driven via /api/tracks/me. We only render when:
 *   • find_instructor_pending === true
 *   • user dismissable; dismissal POSTs /api/tracks/find-instructor/dismiss
 *
 * Tone: supportive, not warning/error based. No red, no urgency, no
 * countdown. Calm white surface matching the new P2-lite pattern.
 */
import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Compass, UserPlus, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StillLookingInstructorBanner({ trackState, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  // null | "dismiss" | "indep"
  const [localHidden, setLocalHidden] = useState(false);

  if (!trackState?.find_instructor_pending) return null;
  if (localHidden) return null;

  const onDismiss = async () => {
    setBusy("dismiss");
    try {
      await axios.post(
        `${API}/api/tracks/find-instructor/dismiss`,
        {},
        { withCredentials: true },
      );
      setLocalHidden(true);
      onChanged?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't dismiss — please try again",
      );
    } finally {
      setBusy(null);
    }
  };

  const onSwitchIndep = async () => {
    setBusy("indep");
    try {
      await axios.post(
        `${API}/api/tracks/set`,
        {
          track: "independent",
          has_instructor: false,
        },
        { withCredentials: true },
      );
      setLocalHidden(true);
      onChanged?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Couldn't switch tracks — please try again",
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      data-testid="still-looking-instructor-banner"
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm relative"
    >
      <button
        type="button"
        data-testid="still-looking-dismiss"
        onClick={onDismiss}
        disabled={busy !== null}
        aria-label="Dismiss"
        className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 transition-colors"
      >
        {busy === "dismiss"
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <X className="w-4 h-4" />}
      </button>
      <div className="pr-7">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400">
          A gentle nudge
        </p>
        <h3
          data-testid="still-looking-title"
          className="text-base sm:text-lg font-extrabold text-slate-900 dark:text-white leading-tight mt-1"
        >
          Still looking for an instructor?
        </h3>
        <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
          You can continue finding an instructor or switch to independent
          learning for now. You can change this any time.
        </p>
      </div>

      <div className="mt-4 space-y-2">
        <button
          type="button"
          data-testid="still-looking-continue"
          disabled={busy !== null}
          onClick={() => navigate("/learn/connect")}
          className="w-full text-left rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/40 dark:bg-indigo-900/10 hover:border-indigo-400 dark:hover:border-indigo-700 disabled:opacity-60 p-3 sm:p-4 flex items-center gap-3 transition-colors group"
        >
          <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
            <Compass className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">
              Continue finding an instructor
            </p>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
              Pick up where you left off
            </p>
          </div>
        </button>

        <button
          type="button"
          data-testid="still-looking-indep"
          disabled={busy !== null}
          onClick={onSwitchIndep}
          className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 disabled:opacity-60 p-3 sm:p-4 flex items-center gap-3 transition-colors group"
        >
          <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
            {busy === "indep"
              ? <Loader2 className="w-4 h-4 animate-spin text-slate-600 dark:text-slate-300" />
              : <UserPlus className="w-4 h-4 text-slate-600 dark:text-slate-300" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">
              Learn independently for now
            </p>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
              Track your own progress and connect later
            </p>
          </div>
        </button>
      </div>
    </section>
  );
}
